import { MutableRefObject, useEffect, useRef, useState } from 'react'
import {
  GridApiPro,
  GridColDef,
  GridValidRowModel,
} from '@microservices/wiskey-react-table-component'
import { GridStateColDef } from '@mui/x-data-grid/internals'

import { ConfiguredColumn, RESTRICTION_TYPE, RestrictionData, RestrictionDTO } from '@types'

export const useHandleColumnDrag = (
  apiRef: MutableRefObject<GridApiPro>,
  entityId: string,
  entityRestrictions: RestrictionData | undefined,
  allColumns: ConfiguredColumn[],
  oldColumns: GridStateColDef<GridValidRowModel, any, any>[],
  handleUpdateRestrictions: (
    restrictions: RestrictionDTO[],
    localRestrictions?: RestrictionDTO[]
  ) => Promise<unknown>,
  isLoadingView?: boolean,
  restrictionColumns?: ConfiguredColumn[]
) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const handleColumnOrderChange = (
    colDef: GridStateColDef<GridValidRowModel, any, any>,
    newColumns: GridStateColDef<GridValidRowModel, any, any>[]
  ) => {
    // Ищем индекс колонки до и после изменений для того, чтобы узнать в какую сторону двигается колонка
    newColumns = newColumns.filter(column => column.pinnedColumn === colDef.pinnedColumn)
    const changedIndex = newColumns.findIndex(column => column.field === colDef.field)
    const oldIndex = startVisibleColumns.current
      .filter(column => {
        const showRestrictions = entityRestrictions?.show.find(
          data => data.fieldCode === column.field
        )

        const isVisible =
          !showRestrictions || showRestrictions.value === true || showRestrictions.value === 'true'

        return column.pinnedColumn === colDef.pinnedColumn && isVisible
      })
      .findIndex(column => column.field === colDef.field)

    // Смотрим на новый массив колонок и смотрим на соседние колонки у целевой
    const leftColumn: GridStateColDef<GridValidRowModel, any, any> | undefined =
      newColumns[changedIndex - 1]
    const rightColumn: GridStateColDef<GridValidRowModel, any, any> | undefined =
      newColumns[changedIndex + 1]
    let targetColumn: GridStateColDef<GridValidRowModel, any, any> | undefined

    if (oldIndex > changedIndex) {
      // Если двигаемся влево, то целимся на колонку справа
      targetColumn = rightColumn
    } else if (oldIndex < changedIndex) {
      // Если двигаемся вправо, то целимся на колонку слева
      targetColumn = leftColumn
    }

    if (targetColumn) {
      const field = targetColumn.field
      const newRestrictions = entityRestrictions?.order.length
        ? entityRestrictions.order
            .map(restr => ({
              fieldCode: restr.fieldCode,
              fieldName: restr.fieldName,
              viewId: entityId as string,
              restrictionType: RESTRICTION_TYPE.ORDER,
              value: restr.value,
            }))
            .sort((a, b) => Number(a.value) - Number(b.value))
        : allColumns.map((col, index) => ({
            fieldCode: col.code,
            fieldName: col.title,
            viewId: entityId as string,
            restrictionType: RESTRICTION_TYPE.ORDER,
            value: index,
          }))
      // Находим значение рестрикшена порядка у соседней колонки
      const oldOrder = Number(
        newRestrictions.find(restriction => restriction.fieldCode === colDef.field)?.value
      )

      // Получаем новый ожидаемый порядок
      const newOrder = Number(
        newRestrictions.find(restriction => restriction.fieldCode === field)?.value
      )

      if (oldOrder === newOrder) {
        return
      }

      let newRestriction

      newRestrictions.map(restriction => {
        if (restriction.fieldCode === colDef.field) {
          restriction.value = newOrder
          newRestriction = restriction

          return restriction
        }
        // Задаем новые рестрикшены порядка для колонок, которые находятся между старыми и новыми значениями порядка
        if (Number(restriction.value) > oldOrder && Number(restriction.value) <= newOrder) {
          restriction.value = Number(restriction.value) - 1
        } else if (Number(restriction.value) < oldOrder && Number(restriction.value) >= newOrder) {
          restriction.value = Number(restriction.value) + 1
        }

        return restriction
      })

      handleUpdateRestrictions([newRestriction], newRestrictions)
    }
  }

  const startVisibleColumns = useRef<GridColDef<any, any, any>[]>([])

  useEffect(() => {
    if (isLoadingView || !apiRef || !apiRef.current?.subscribeEvent) {
      return
    }

    const cleanUpDrugStart = apiRef.current.subscribeEvent('columnHeaderDragStart', data => {
      if (restrictionColumns && !restrictionColumns.some(item => item.code === data.field)) {
        return
      }

      setIsDragActive(true)
      startVisibleColumns.current = apiRef?.current?.getVisibleColumns() || []
    })

    const cleanUpCallback = apiRef.current.subscribeEvent(
      'columnHeaderDragEnd',
      ({ colDef, field }) => {
        if (restrictionColumns && !restrictionColumns.some(item => item.code === field)) {
          return
        }

        setIsDragActive(false)
        handleColumnOrderChange(colDef, apiRef?.current?.getVisibleColumns())
      }
    )

    return () => {
      cleanUpDrugStart()
      cleanUpCallback()
    }
  }, [
    apiRef,
    entityRestrictions,
    isLoadingView,
    entityId,
    allColumns,
    oldColumns,
    handleColumnOrderChange,
  ])

  return isDragActive
}
